<template>
    <div id="recipe-container" v-if="recipe">
        <div class="recipe-head" v-if="!hide_image">
            <img :src="imageUrl + '/images/recipe_images/' + recipe.image" />
        </div>
        <div id="recipe-body">
            <div class="recipe-title d-flex">
                <div>
                    <h3>{{ recipe.title }}</h3>
                    <p>Serves: {{ recipe.servings }}</p>
                </div>
                <multiply-controls v-if="isAdmin"></multiply-controls>
            </div>
            <div class="recipe-nutrition">
                <h6>Nutrition (per serve)</h6>
                <div class="nutri-item">
                    <p><span class="recipe-macros">Calories (cals)</span></p>
                    <p><span class="recipe-macros-res">{{ total('calories') }}</span></p>
                </div>
                <div class="nutri-item">
                    <p><span class="recipe-macros">Protein (gms)</span></p>
                    <p><span class="recipe-macros-res">{{ total('protein') }}</span></p>
                </div>
                <div class="nutri-item">
                    <p><span class="recipe-macros">Total Fat (gms)</span></p>
                    <p><span class="recipe-macros-res">{{ total('total_fat') }}</span></p>
                </div>
                <div class="nutri-item">
                    <p><span class="recipe-macros">Carbs (gms)</span></p>
                    <p><span class="recipe-macros-res">{{ total('carbs') }}</span></p>
                </div>
            </div>
            <div class="recipe-ingredients">
                <template v-for="(ingredient_list, list_index) in recipe.ingredient_lists">
                    <h6>{{ ingredient_list.title }}</h6>
                    <ul>
                        <drop-list
                            :items="ingredients(list_index)"
                            class="list"
                            @reorder="reorderIngredients(list_index, $event)">
                            <template v-slot:item="{item}">
                                <drag v-if="item" class="item" :key="item.id" :disabled="!isAdmin">
                                    <li>
                                        <recipe-ingredient :ingredient="item" :inline="inline"></recipe-ingredient>
                                    </li>
                                </drag>
                            </template>
                        </drop-list>
                    </ul>
                </template>
            </div>
            <div class="recipe-method">
                <div v-for="(method_list, methods_index) in recipe.method_lists">
                    <h6>{{ method_list.title }}</h6>
                    <ol>
                        <drop-list
                            :items="methods(methods_index)"
                            class="list"
                            @reorder="reorderMethods(methods_index, $event)"
                        >
                            <template v-slot:item="{item}">
                                <drag v-if="item" class="item" :key="item.id" :disabled="!isAdmin">
                                    <li>
                                        {{ item.description }}
                                    </li>
                                </drag>
                            </template>
                        </drop-list>
                    </ol>
                </div>
                <div class="recipe-notes multiply" v-if="multiply > 1">
                    <h6>Warning</h6>
                    <p>You are multiplying this recipe by {{multiply}}. Please note that some cooking times and methods may vary.</p>
                </div>
                <div class="recipe-notes" v-if="notes && notes.text && notes.text.length > 0 && notes.text !== 'null'">
                    <h6>Notes</h6>
                    <p>{{ notes.text }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import RecipeIngredient from "./RecipeIngredient";
    import MultiplyControls from "./MultiplyControls";
    import { Drag, DropList } from "vue-easy-dnd";

    export default {
        components: {MultiplyControls, RecipeIngredient, Drag, DropList},
        props: ['inline', 'hide_image'],
        name: "Recipe",
        mounted () {
            if (this.$route.params.recipe_id && parseInt(this.recipe.id) !== parseInt(this.$route.params.recipe_id)) this.$store.dispatch('Recipe/loadRecipe', this.$route.params.recipe_id)
            if (!this.$route.params.recipe_id) this.$store.dispatch('Recipe/loadRecipe', 0)
        },
        computed: {
            ...mapGetters('Ingredients', ['total']),
            ...mapGetters('Recipe', ['recipe', 'notes']),
            ...mapGetters('Streams', ['stream']),
            ...mapGetters('Settings', ['imageUrl', 'multiply', 'isAdmin']),
        },
        methods: {
            reorderMethods (index, e) {
                let payload = e
                payload.list_index = index
                this.$store.dispatch('Methods/reorder', payload)
                this.$forceUpdate()
            },
            reorderIngredients (index, e) {
                let payload = e
                payload.list_index = index
                payload.item = this.ingredients(index)[e.from]
                this.$store.dispatch('Ingredients/reorder', payload)
                this.$forceUpdate()
            },
            ingredients (index) {
                if(!this.recipe) return []
                return this.$store.getters['Ingredients/ingredients'](index)
            },
            methods (index) {
                if(!this.recipe) return []
                return this.$store.getters['Methods/methods'](index)
            },
        }
    }
</script>

<style lang="scss">
    // flex align to the top on blank layout
    body.body__recipe {
        align-items: start !important;
    }
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500&display=swap');
    #recipe-container {
        all: initial; // reset everything so no other styles effect
        margin: 0 auto;
        font-size: 22px !important;
        // start styling
        font-family: Roboto, sans-serif;
        .recipe-head {
            width: 100%;
            position: relative;
            padding-top: 60%;
            overflow: hidden;
            img {
                position: absolute;
                top: 0;
                object-fit: cover;
                width: 100%;
            }
        }
        #recipe-body {
            display: flex;
            flex-flow: row wrap;
            flex: 1 100%;
            padding: 15px 30px 15px 30px;
            h6 {
                //font-family: "DIN Alternate Bold", Roboto, sans-serif;
                text-transform: uppercase;
                margin-bottom: 15px;
                color:#555;
            }
            .recipe-title {
                flex: 1 100%;
                h3 {
                    color: #0497a3;
                    margin: 0;
                }
                .recipe-print-btn {
                    text-align: right;
                    margin-bottom: 0;
                    a {
                        color: #222;
                    }
                }
                p {
                    color: #979797;
                    text-transform: uppercase;
                    font-size: .7em;
                }
            }
            .recipe-ingredients {
                background-color: #CDEAED;
                width: 100%;
                padding: 20px;
                border-radius: 4px;
                ul {
                    padding-left: 10px;
                    list-style-type:none;
                    counter-reset:ingredients-counter;
                    li {
                        margin:0 0 10px 15px;
                        position: relative;
                        font-size:14px;
                        font-weight: 300;
                        line-height:1.3em;
                        &:before {
                            content:"";
                            position:absolute;
                            background: #0497a3;
                            width:10px;
                            height:10px;
                            left:-28px;
                            margin: 4px 5px;
                            //border-radius:4px;
                            //border:thin solid rgba(0,0,0,.1);
                            //box-shadow:0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
                        }
                    }
                }
            }
            .recipe-method {
                flex: 1;
                padding: 20px 10px 10px 10px;
                font-weight:300;
                padding-bottom: 0;
                display: flex;
                flex-flow: column;
                justify-content: space-between;
                ol {
                    margin-bottom:3em;
                    list-style-type:none;
                    counter-reset:method-counter;
                    li {
                        margin: 0 0 25px 10px;
                        position: relative;
                        line-height:1.3em;
                        font-size:14px;
                        &:before {
                            content:counter(method-counter);
                            counter-increment:method-counter;
                            position:absolute;
                            background:#0497a3;
                            color:#fff;
                            font-weight:500;
                            text-align:center;
                            border-radius: 50%;
                            left:-40px;
                            width:25px;
                            height:25px;
                            line-height:25px;
                        }
                    }
                }
                .recipe-notes {
                    border-radius:4px;
                    padding: 15px;
                    background-color: rgba(240, 171, 29, .2);
                    &.multiply {
                        margin-bottom: 20px;
                        background-color: rgba(255, 0, 0, .2);
                    }
                    p {
                        font-family: Roboto, sans-serif;
                        margin-bottom: 10px;
                        font-size:14px;
                        .dietary-pref {
                            font-size:1.25rem;
                            color:rgba(0,0,0,.6);
                        }
                    }
                }
            }
            .recipe-nutrition {
                background-color:rgba(4, 151, 163, 0.2);
                border-radius: 4px;
                background-color:#fff;
                border:thin solid rgba(0,0,0,.12);
                flex: 1 100%;
                display:flex;
                flex-wrap:wrap;
                justify-content:space-between;
                padding:10px 15px;
                margin-bottom:10px;
                h6 {
                    width:100%;
                }
                .nutri-item {
                    border-left:4px solid rgba(4, 151, 163, 0.5);
                    padding:0 15px 0 15px;
                    margin-bottom:5px;
                    min-width: 90px;
                    p {
                        margin-bottom:0;
                    }
                    .recipe-macros {
                        color:rgba(0,0,0,.4);
                        font-size:.6em;
                    }
                    .recipe-macros-res {
                        font-size:1.3em;
                        font-weight:bold;
                        color:rgba(4, 151, 163, 0.5);
                    }
                }
            }
            @media only screen and (min-width:768px) {
                .recipe-ingredients {
                    width: 33% !important;
                }
                .recipe-method {
                    padding: 20px;
                    padding-bottom: 0;
                }
                .recipe-nutrition {
                    .nutri-item {
                        margin-bottom:0;
                    }
                }
            }
            @media print {
                #recipe-body {
                    display:flex;
                    flex-wrap:wrap;
                }
                .recipe-print-btn {
                    display:none;
                }
                .recipe-ingredients {
                    width: 100% !important;
                }
            }
        }
    }
</style>
